import fs from "fs";
import path from "path";
import { RegisteredTool, ToolHandler, ExecutionContext, ToolResult } from "../registry";
import { withinCwdSafe } from "../files";

// Edit File Tool (partial editing)
const editFileHandler: ToolHandler = {
  async execute(
    params: { 
      path: string; 
      search: string; 
      replace: string; 
      line_number?: number;
      backup?: boolean;
    }, 
    context: ExecutionContext
  ): Promise<ToolResult> {
    const { path: filePath, search, replace, line_number, backup = true } = params;
    
    if (!withinCwdSafe(filePath, context.workingDirectory)) {
      return {
        success: false,
        error: `Path outside workspace: ${filePath}`
      };
    }

    const fullPath = path.resolve(context.workingDirectory, filePath);
    
    if (!fs.existsSync(fullPath)) {
      return {
        success: false,
        error: `File not found: ${filePath}`
      };
    }

    try {
      const originalContent = fs.readFileSync(fullPath, 'utf8');
      let newContent: string;
      let changes = 0;

      if (line_number !== undefined) {
        // Edit specific line number
        const lines = originalContent.split('\n');
        if (line_number < 1 || line_number > lines.length) {
          return {
            success: false,
            error: `Line number ${line_number} is out of range (1-${lines.length})`
          };
        }
        
        const oldLine = lines[line_number - 1];
        lines[line_number - 1] = oldLine.replace(search, replace);
        
        if (lines[line_number - 1] !== oldLine) {
          changes = 1;
        }
        
        newContent = lines.join('\n');
      } else {
        // Global search and replace
        const searchRegex = new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        newContent = originalContent.replace(searchRegex, replace);
        
        const matches = originalContent.match(searchRegex);
        changes = matches ? matches.length : 0;
      }

      // Create backup if requested
      if (backup && changes > 0) {
        const backupPath = `${fullPath}.bak`;
        fs.writeFileSync(backupPath, originalContent, 'utf8');
      }

      if (changes > 0) {
        fs.writeFileSync(fullPath, newContent, 'utf8');
      }

      return {
        success: true,
        content: `Made ${changes} changes to ${filePath}`,
        metadata: {
          path: filePath,
          changes,
          line_number: line_number || null,
          search_pattern: search,
          replacement: replace,
          backup_created: backup && changes > 0
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to edit file: ${error.message}`
      };
    }
  }
};

export const editFileTool: RegisteredTool = {
  name: "edit_file",
  description: "Edit file content with search and replace",
  schema: {
    type: "object",
    properties: {
      path: {
        type: "string",
        description: "Path to file to edit"
      },
      search: {
        type: "string",
        description: "Text to search for"
      },
      replace: {
        type: "string",
        description: "Replacement text"
      },
      line_number: {
        type: "number",
        description: "Specific line number to edit (1-based)"
      },
      backup: {
        type: "boolean",
        description: "Create backup file",
        default: true
      }
    },
    required: ["path", "search", "replace"]
  },
  safety: {
    require_approval: true,
    path_restrictions: ["!node_modules", "!.git", "!dist"],
    network_access: false,
    max_execution_time: 10000,
    allowed_in_ci: false
  },
  handler: editFileHandler,
  metadata: {
    category: "file_operations",
    version: "1.0",
    author: "metis-team"
  }
};

// Append to File Tool
const appendToFileHandler: ToolHandler = {
  async execute(
    params: { 
      path: string; 
      content: string; 
      newline?: boolean;
      create?: boolean;
    }, 
    context: ExecutionContext
  ): Promise<ToolResult> {
    const { path: filePath, content, newline = true, create = false } = params;
    
    if (!withinCwdSafe(filePath, context.workingDirectory)) {
      return {
        success: false,
        error: `Path outside workspace: ${filePath}`
      };
    }

    const fullPath = path.resolve(context.workingDirectory, filePath);
    
    if (!fs.existsSync(fullPath) && !create) {
      return {
        success: false,
        error: `File not found: ${filePath}. Set create=true to create new file.`
      };
    }

    try {
      const contentToAppend = newline ? `\n${content}` : content;
      fs.appendFileSync(fullPath, contentToAppend, 'utf8');
      
      const stats = fs.statSync(fullPath);
      
      return {
        success: true,
        content: `Appended ${content.length} characters to ${filePath}`,
        metadata: {
          path: filePath,
          content_length: content.length,
          newline_added: newline,
          file_size: stats.size,
          created: create && !fs.existsSync(fullPath)
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to append to file: ${error.message}`
      };
    }
  }
};

export const appendToFileTool: RegisteredTool = {
  name: "append_to_file",
  description: "Append content to end of file",
  schema: {
    type: "object",
    properties: {
      path: {
        type: "string",
        description: "Path to file"
      },
      content: {
        type: "string",
        description: "Content to append"
      },
      newline: {
        type: "boolean",
        description: "Add newline before content",
        default: true
      },
      create: {
        type: "boolean",
        description: "Create file if it doesn't exist",
        default: false
      }
    },
    required: ["path", "content"]
  },
  safety: {
    require_approval: true,
    path_restrictions: ["!node_modules", "!.git", "!dist"],
    network_access: false,
    max_execution_time: 10000,
    allowed_in_ci: false
  },
  handler: appendToFileHandler,
  metadata: {
    category: "file_operations",
    version: "1.0",
    author: "metis-team"
  }
};

// Create Directory Tool
const createDirectoryHandler: ToolHandler = {
  async execute(
    params: { 
      path: string; 
      recursive?: boolean;
    }, 
    context: ExecutionContext
  ): Promise<ToolResult> {
    const { path: dirPath, recursive = true } = params;
    
    if (!withinCwdSafe(dirPath, context.workingDirectory)) {
      return {
        success: false,
        error: `Path outside workspace: ${dirPath}`
      };
    }

    const fullPath = path.resolve(context.workingDirectory, dirPath);
    
    if (fs.existsSync(fullPath)) {
      if (fs.statSync(fullPath).isDirectory()) {
        return {
          success: true,
          content: `Directory already exists: ${dirPath}`,
          metadata: {
            path: dirPath,
            created: false,
            already_existed: true
          }
        };
      } else {
        return {
          success: false,
          error: `Path exists but is not a directory: ${dirPath}`
        };
      }
    }

    try {
      fs.mkdirSync(fullPath, { recursive });
      
      return {
        success: true,
        content: `Created directory: ${dirPath}`,
        metadata: {
          path: dirPath,
          created: true,
          recursive
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to create directory: ${error.message}`
      };
    }
  }
};

export const createDirectoryTool: RegisteredTool = {
  name: "create_directory",
  description: "Create a new directory",
  schema: {
    type: "object",
    properties: {
      path: {
        type: "string",
        description: "Path of directory to create"
      },
      recursive: {
        type: "boolean",
        description: "Create parent directories if they don't exist",
        default: true
      }
    },
    required: ["path"]
  },
  safety: {
    require_approval: false,
    path_restrictions: ["!node_modules", "!.git", "!dist"],
    network_access: false,
    max_execution_time: 5000,
    allowed_in_ci: true
  },
  handler: createDirectoryHandler,
  metadata: {
    category: "file_operations",
    version: "1.0",
    author: "metis-team"
  }
};

// Move/Rename File Tool
const moveFileHandler: ToolHandler = {
  async execute(
    params: { 
      source: string; 
      destination: string; 
      overwrite?: boolean;
    }, 
    context: ExecutionContext
  ): Promise<ToolResult> {
    const { source, destination, overwrite = false } = params;
    
    if (!withinCwdSafe(source, context.workingDirectory) || 
        !withinCwdSafe(destination, context.workingDirectory)) {
      return {
        success: false,
        error: `Path outside workspace: ${source} or ${destination}`
      };
    }

    const sourcePath = path.resolve(context.workingDirectory, source);
    const destPath = path.resolve(context.workingDirectory, destination);
    
    if (!fs.existsSync(sourcePath)) {
      return {
        success: false,
        error: `Source file not found: ${source}`
      };
    }

    if (fs.existsSync(destPath) && !overwrite) {
      return {
        success: false,
        error: `Destination already exists: ${destination}. Set overwrite=true to replace.`
      };
    }

    try {
      // Create destination directory if it doesn't exist
      const destDir = path.dirname(destPath);
      if (!fs.existsSync(destDir)) {
        fs.mkdirSync(destDir, { recursive: true });
      }

      fs.renameSync(sourcePath, destPath);
      
      const stats = fs.statSync(destPath);
      
      return {
        success: true,
        content: `Moved ${source} to ${destination}`,
        metadata: {
          source,
          destination,
          overwrite,
          size: stats.size,
          is_directory: stats.isDirectory()
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: `Failed to move file: ${error.message}`
      };
    }
  }
};

export const moveFileTool: RegisteredTool = {
  name: "move_file",
  description: "Move or rename a file or directory",
  schema: {
    type: "object",
    properties: {
      source: {
        type: "string",
        description: "Source path"
      },
      destination: {
        type: "string", 
        description: "Destination path"
      },
      overwrite: {
        type: "boolean",
        description: "Overwrite destination if it exists",
        default: false
      }
    },
    required: ["source", "destination"]
  },
  safety: {
    require_approval: true,
    path_restrictions: ["!node_modules", "!.git", "!dist"],
    network_access: false,
    max_execution_time: 10000,
    allowed_in_ci: false
  },
  handler: moveFileHandler,
  metadata: {
    category: "file_operations",
    version: "1.0",
    author: "metis-team"
  }
};